import React, { useEffect, useState } from 'react';
import './AdminSettingsPage.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { confirmDialog, ConfirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import ImportFile from '../../../shared/components/common/import-file/ImportFile';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import {
  getBlockedIps,
  addBlockedIp,
  removeBlockedIp,
  toggleConfiguration,
  BlockedIp,
} from '../../../shared/services/admin/dashboard/AdminDashboardService';
import { getConfiguration } from '../../../shared/services/admin/dashboard/AdminDashboardService';
import { AdminConfigurationResponse } from '../../../shared/models/admin/dashboard/AdminConfigurationResponse';
import { format } from 'date-fns';

const AdminSettingsPage: React.FC = () => {
  let confirmRef: ConfirmDialogReturn;

  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [configurations, setConfigurations] = useState<AdminConfigurationResponse[]>([]);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [ips, configs] = await Promise.all([getBlockedIps(), getConfiguration()]);
    setBlockedIps(ips);
    setConfigurations(configs);
  };

  const isEmailVerificationEnabled =
    configurations.find((c) => c.feature === 'EMAIL_VERIFICATION')?.isEnabled ?? false;

  const handleToggleEmailVerification = async () => {
    await toggleConfiguration('EMAIL_VERIFICATION', !isEmailVerificationEnabled);
    await loadData();
  };

  const handleAddIp = async () => {
    if (!newIp.trim()) return;
    setAddError(null);
    try {
      await addBlockedIp(newIp.trim(), newReason.trim() || null);
      setNewIp('');
      setNewReason('');
      await loadData();
    } catch (err: any) {
      setAddError(err?.response?.data?.error || 'Failed to add IP');
    }
  };

  const handleRemoveIp = async (id: number) => {
    await removeBlockedIp(id);
    await loadData();
  };

  const hide = () => {
    if (confirmRef) {
      confirmRef.hide();
    }
  };

  const importFileDialog = () => {
    confirmRef = confirmDialog({
      message: <ImportFile importEndCallback={() => hide()} />,
      header: 'Import from file',
      footer: () => null,
    });
  };

  return (
    <>
      <ConfirmDialog className="dialog-box" />
      <div className="settings">
        <ul>
          <li>
            <Button className="btn btn--link" label="Clean old entry (Disable)" />
          </li>
          <li>
            <Button className="btn btn--link" label="Create user account (Disable)" />
          </li>
          <li>
            <Button className="btn btn--link" label="Import from file" onClick={importFileDialog} />
          </li>
        </ul>

        <div className="settings__section">
          <div className="page-box__title-action">
            <div className="page-box__title">Email Verification</div>
          </div>
          <div className="form-box__checkbox">
            <Checkbox
              onChange={handleToggleEmailVerification}
              checked={isEmailVerificationEnabled}
            />
            <span>Enable email verification for new registrations</span>
          </div>
        </div>

        <div className="settings__section">
          <div className="page-box__title-action">
            <div className="page-box__title">Blocked IPs</div>
          </div>
          <div className="settings__add-ip-row">
            <InputText
              value={newIp}
              onChange={(e) => {
                setNewIp(e.target.value);
                setAddError(null);
              }}
              placeholder="IP Address"
              className={addError ? 'form-box__invalid' : ''}
            />
            <InputText
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="Reason (optional)"
            />
            <Button className="btn btn--sm" label="Add" onClick={handleAddIp} />
          </div>
          {addError && <div className="p-error">{addError}</div>}

          <DataTable
            value={blockedIps}
            tableClassName="table"
            className="table-wrapper"
            tableStyle={{ width: '100%' }}
            stripedRows
            emptyMessage={() => <EmptyPanel />}
          >
            <Column
              field="ip"
              header="IP Address"
              headerClassName="table__header"
              style={{ width: '180px' }}
            />
            <Column
              field="reason"
              header="Reason"
              headerClassName="table__header"
              body={(row: BlockedIp) => row.reason || '-'}
            />
            <Column
              field="createdAt"
              header="Created"
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '160px' }}
              body={(row: BlockedIp) => {
                try {
                  return format(new Date(row.createdAt), 'yyyy-MM-dd HH:mm');
                } catch {
                  return row.createdAt;
                }
              }}
            />
            <Column
              header="Actions"
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '100px' }}
              body={(row: BlockedIp) => (
                <Button
                  className="btn btn--link-red btn--sm"
                  label="Delete"
                  onClick={() => handleRemoveIp(row.id)}
                />
              )}
            />
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default AdminSettingsPage;
